import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { Copy, Gift, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { GET_MY_REFERRAL_PROGRAM } from "@/graphql/queries";

interface ReferralDialogProps {
  collapsed?: boolean;
}

interface ReferralProgramResponse {
  myReferralProgram: {
    code: string;
    shareURL: string;
    pendingReferrals: number;
    completedReferrals: number;
    earnedCredits: number;
  };
}

export function ReferralDialog({ collapsed = false }: ReferralDialogProps) {
  const { data, loading } = useQuery<ReferralProgramResponse>(GET_MY_REFERRAL_PROGRAM, {
    fetchPolicy: "cache-and-network",
  });

  const referralProgram = data?.myReferralProgram;
  const summary = useMemo(() => {
    if (!referralProgram) {
      return {
        code: "",
        shareURL: "",
        pendingReferrals: 0,
        completedReferrals: 0,
        earnedCredits: 0,
      };
    }
    return referralProgram;
  }, [referralProgram]);

  const copyValue = async (value: string, label: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className={collapsed ? "h-11 w-full justify-center px-0" : "h-11 w-full justify-start gap-3 px-3"}
          title={collapsed ? "Referrals" : undefined}
        >
          <Gift className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Referrals</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refer and Earn</DialogTitle>
          <DialogDescription>
            When someone signs up and makes a purchase with your referral code, you earn 500 credits.
          </DialogDescription>
        </DialogHeader>

        {loading && !referralProgram ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading referral details...
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium">Referral link</p>
              <div className="flex gap-2">
                <Input value={summary.shareURL} readOnly className="h-10" />
                <Button type="button" variant="outline" size="icon" onClick={() => copyValue(summary.shareURL, "Link")}>
                  <Link2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Referral code</p>
              <div className="flex gap-2">
                <Input value={summary.code} readOnly className="h-10 font-mono uppercase" />
                <Button type="button" variant="outline" size="icon" onClick={() => copyValue(summary.code, "Code")}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
